import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  AccountCircle as AccountCircleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { createApiClient } from 'api/apiClient';
import { CompletePatientDataPayload } from 'api/apiClient';
import './DataCompletionQueue.css';

/*  Types  */

interface DataGapSummary {
  total_patients: number;
  complete: number;
  incomplete: number;
  ssn_issues: number;
  field_issues: number;
}

interface PatientGapRow {
  id: number;
  internal_id?: number;
  first_name: string;
  last_name: string;
  middle_name?: string;
  dob: string;
  ssn?: string;
  ssn_status?: string;
  email?: string;
  gender?: string;
  username?: string;
  home_tel?: string;
  home_tel_country_code?: string;
  cell?: string;
  cell_country_code?: string;
  home_addr_1?: string;
  home_addr_2?: string;
  address_city?: string;
  address_zip?: string;
  state?: string;
  drive_license_number?: string;
  ref_uid?: string | null;
  remote_monitoring?: string;
  billing_permission?: string;
  device_pairing?: string;
  start_date?: string;
  program_id?: number | null;
  issues: string[];
  missing_fields?: string[];
  editable_fields?: string[];
  field_errors?: Record<string, string>;
  data_complete: boolean;
}

interface FieldErrors {
  [key: string]: string | undefined;
}

type ToastSeverity = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  open: boolean;
  message: string;
  severity: ToastSeverity;
}

/*  Helpers  */

const ISSUE_TYPE_OPTIONS = [
  { value: 'all', label: 'All Issues' },
  { value: 'missing_ssn', label: 'Missing SSN' },
  { value: 'invalid_ssn', label: 'Invalid SSN' },
  { value: 'missing_profile_fields', label: 'Missing Profile Fields' },
];

const ITEMS_PER_PAGE = 20;

function getBadgeClass(issue: string): string {
  const lower = issue.toLowerCase();
  if (lower.includes('missing') && lower.includes('ssn')) return 'missing-ssn';
  if (lower.includes('invalid') && lower.includes('ssn')) return 'invalid-ssn';
  if (lower.includes('missing') || lower.includes('profile') || lower.includes('field')) return 'missing-field';
  return 'unknown';
}

function formatDob(dob?: string): string {
  if (!dob) return '—';
  // Attempt ISO → MM/DD/YYYY
  const d = new Date(dob);
  if (isNaN(d.getTime())) return dob;
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

/*  Stat Card  */

interface StatCardProps {
  label: string;
  value: number | undefined;
  loading: boolean;
  colorClass: 'blue' | 'orange' | 'red' | 'purple';
  icon: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, loading, colorClass, icon }) => (
  <div className="dcq-stat-card">
    <div className={`dcq-stat-icon ${colorClass}`}>{icon}</div>
    <div className="dcq-stat-label">{label}</div>
    {loading ? (
      <div className="dcq-stat-loading" />
    ) : (
      <div className="dcq-stat-value">{value ?? '—'}</div>
    )}
  </div>
);

/*  Complete Data Modal  */

/** Metadata for every field we might display in the modal */
const PATIENT_FIELD_META: Array<{
  key: string;
  label: string;
  type?: 'text' | 'ssn' | 'date';
}> = [
    { key: 'first_name', label: 'First Name' },
    { key: 'middle_name', label: 'Middle Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'dob', label: 'Date of Birth', type: 'date' },
    { key: 'gender', label: 'Gender' },
    { key: 'ssn', label: 'SSN', type: 'ssn' },
    { key: 'email', label: 'Email' },
    { key: 'username', label: 'Username' },
    { key: 'home_tel', label: 'Home Phone' },
    { key: 'cell', label: 'Cell Phone' },
    { key: 'home_addr_1', label: 'Address Line 1' },
    { key: 'home_addr_2', label: 'Address Line 2' },
    { key: 'address_city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'address_zip', label: 'Zip Code' },
    { key: 'drive_license_number', label: "Driver's License" },
    { key: 'ref_uid', label: 'Reference ID' },
    { key: 'remote_monitoring', label: 'Remote Monitoring' },
    { key: 'device_pairing', label: 'Device Pairing' },
    { key: 'start_date', label: 'Start Date' },
  ];

function getPatientFieldValue(patient: PatientGapRow, key: string): string {
  const raw = (patient as any)[key];
  if (raw === null || raw === undefined || raw === '') return '';
  return String(raw);
}

interface CompleteDataModalProps {
  open: boolean;
  patient: PatientGapRow | null;
  onClose: () => void;
  onSuccess: (patientId: number) => void;
  onToast: (msg: string, severity: ToastSeverity) => void;
}

const CompleteDataModal: React.FC<CompleteDataModalProps> = ({
  open,
  patient,
  onClose,
  onSuccess,
  onToast,
}) => {
  const accessToken = localStorage.getItem('access_token');
  const controller = new AbortController();
  const apiClient = createApiClient(accessToken!, controller);

  const [form, setForm] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);

  // Derive which fields are editable from the patient's editable_fields list
  const editableFields = patient?.editable_fields ?? [];

  // Pre-fill form with current values for editable fields only
  useEffect(() => {
    if (patient) {
      const initial: Record<string, string> = {};
      editableFields.forEach(key => {
        initial[key] = getPatientFieldValue(patient, key);
      });
      setForm(initial);
      setFieldErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient]);

  const handleChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [key]: e.target.value }));
    setFieldErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const handleSsnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 9);
    setForm(prev => ({ ...prev, ssn: digitsOnly }));
    setFieldErrors(prev => ({ ...prev, ssn: undefined }));
  };

  const handleSave = async () => {
    if (!patient) return;

    // Validate SSN only if it's an editable field
    if (editableFields.includes('ssn') && !/^\d{9}$/.test(form.ssn ?? '')) {
      setFieldErrors(prev => ({ ...prev, ssn: 'SSN must be exactly 9 digits.' }));
      return;
    }

    setSaving(true);
    setFieldErrors({});
    try {
      await apiClient.completePatientData(patient.id, form as any);
      onSuccess(patient.id);
      onToast('Patient data completed successfully.', 'success');
      onClose();
    } catch (err: any) {
      const response = err?.response;
      const status = response?.status;
      const data = response?.data;
      const errorCode = data?.error_code ?? data?.errorCode ?? data?.code;

      if (errorCode === 103 || errorCode === '103') {
        const issues: FieldErrors = {};
        if (data?.issues) {
          const raw: string[] = Array.isArray(data.issues)
            ? data.issues
            : Object.values(data.issues).flat() as string[];
          raw.forEach((msg: string) => {
            const lower = msg.toLowerCase();
            if (lower.includes('first_name') || lower.includes('first name')) issues.first_name = msg;
            else if (lower.includes('last_name') || lower.includes('last name')) issues.last_name = msg;
            else if (lower.includes('ssn')) issues.ssn = msg;
            else if (lower.includes('dob') || lower.includes('date of birth')) issues.dob = msg;
            else if (lower.includes('zip')) issues.address_zip = msg;
            else if (lower.includes('phone') || lower.includes('cell') || lower.includes('home_tel')) issues.home_tel = msg;
            else issues.general = issues.general ? `${issues.general}; ${msg}` : msg;
          });
        } else {
          issues.general = data?.message ?? 'Data is incomplete. Please review the fields.';
        }
        setFieldErrors(issues);
      } else if (errorCode === 104 || errorCode === '104') {
        setFieldErrors({ ssn: 'SSN already belongs to another patient.' });
      } else if (status === 422) {
        onToast('Validation error. Please check the form values and try again.', 'error');
      } else {
        onToast(data?.message ?? 'An unexpected error occurred. Please try again.', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const inputSx = {
    '& .MuiInputBase-root': { borderRadius: '8px' },
  };

  const readOnlyRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: '1px solid #f1f5f9',
    fontSize: '13px',
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', p: 0.5 } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: '#1e293b',
          fontSize: '17px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <span>
          Complete Patient Data
          {patient && (
            <span style={{ fontWeight: 400, fontSize: '13px', color: '#64748b', marginLeft: 8 }}>
              — {patient.first_name} {patient.last_name}
            </span>
          )}
        </span>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          maxHeight: '70vh',
          overflowY: 'auto',
        }}
      >
        {fieldErrors.general && (
          <Alert severity="error" sx={{ borderRadius: '8px', fontSize: '13px', mb: 1.5 }}>
            {fieldErrors.general}
          </Alert>
        )}

        {patient && (
          <>
            {/* Issues banner */}
            {patient.issues && patient.issues.length > 0 && (
              <Alert
                severity="warning"
                sx={{ borderRadius: '8px', fontSize: '12.5px', mb: 2, mt: 1 }}
              >
                <strong>Issues detected:</strong>{' '}
                {patient.issues.join(' · ')}
              </Alert>
            )}

            {/* Read-only info section */}
            <div
              style={{
                background: '#f8fafc',
                borderRadius: '10px',
                padding: '10px 14px',
                marginBottom: '16px',
              }}
            >
              {PATIENT_FIELD_META.map(({ key, label }) => {
                const isEditable = editableFields.includes(key);
                if (isEditable) return null; // editable fields rendered below
                const value = getPatientFieldValue(patient, key);
                if (!value) return null; // skip empty fields in read-only section
                return (
                  <div key={key} style={readOnlyRowStyle}>
                    <span style={{ color: '#64748b', fontWeight: 500, minWidth: 140 }}>
                      {label}
                    </span>
                    <span
                      style={{
                        color: '#1e293b',
                        fontWeight: 400,
                        textAlign: 'right',
                        wordBreak: 'break-all',
                      }}
                    >
                      {value}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Editable fields — only those in editable_fields */}
            {editableFields.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#ef4444',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '10px',
                  }}
                >
                  Fields requiring correction
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {PATIENT_FIELD_META.filter(f => editableFields.includes(f.key)).map(
                    ({ key, label, type }) => {
                      const errorMsg =
                        fieldErrors[key] ?? patient.field_errors?.[key];
                      return (
                        <div key={key}>
                          {type === 'ssn' ? (
                            <TextField
                              label={label}
                              value={form[key] ?? ''}
                              onChange={handleSsnChange}
                              fullWidth
                              size="small"
                              inputProps={{
                                inputMode: 'numeric',
                                maxLength: 9,
                                pattern: '[0-9]{9}',
                              }}
                              placeholder="Enter full 9-digit SSN"
                              error={!!fieldErrors[key]}
                              sx={inputSx}
                            />
                          ) : (
                            <TextField
                              label={label}
                              value={form[key] ?? ''}
                              onChange={handleChange(key)}
                              fullWidth
                              size="small"
                              error={!!fieldErrors[key]}
                              sx={inputSx}
                            />
                          )}
                          {errorMsg && (
                            <div className="dcq-field-error" style={{ marginTop: 3 }}>
                              {errorMsg}
                            </div>
                          )}
                        </div>
                      );
                    },
                  )}
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, mt: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={saving}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#cbd5e1',
            color: '#475569',
            '&:hover': { borderColor: '#94a3b8', background: '#f8fafc' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || editableFields.length === 0}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #1f8cff 0%, #0070e0 100%)',
            boxShadow: '0 2px 8px rgba(31,140,255,0.35)',
            '&:hover': { background: 'linear-gradient(135deg, #0070e0 0%, #005bb5 100%)' },
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/*  Main Page  */

const DataCompletionQueue: React.FC = () => {
  const accessToken = localStorage.getItem('access_token');
  const controller = new AbortController();
  const apiClient = createApiClient(accessToken!, controller);

  // Summary
  const [summary, setSummary] = useState<DataGapSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Table data
  const [rows, setRows] = useState<PatientGapRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(true);
  const [tableError, setTableError] = useState(false);

  // Filters
  const [searchName, setSearchName] = useState('');
  const [issueType, setIssueType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientGapRow | null>(null);

  // Toast
  const [toast, setToast] = useState<Toast>({ open: false, message: '', severity: 'success' });

  // ---- Data fetchers ----

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const data = await apiClient.getPatientDataGapsSummary();
      setSummary(data?.summary || data);
    } catch {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchRows = useCallback(
    async (page = currentPage, name = searchName, issue = issueType) => {
      setTableLoading(true);
      setTableError(false);
      try {
        const data = await apiClient.getPatientDataGaps(page, name || undefined, issue);
        // Normalise to array regardless of API response shape
        const rawRows = data?.data ?? data;
        const safeRows: PatientGapRow[] = Array.isArray(rawRows) ? rawRows : [];
        setRows(safeRows);
        setTotalCount(data?.total_count ?? safeRows.length);
      } catch {
        setTableError(true);
        setRows([]);
      } finally {
        setTableLoading(false);
      }
    },
    [currentPage, searchName, issueType],
  );

  useEffect(() => {
    fetchSummary();
    fetchRows(1, searchName, issueType);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch rows when page changes
  useEffect(() => {
    fetchRows(currentPage, searchName, issueType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchRows(1, searchName, issueType);
  };

  const handleRefresh = () => {
    fetchSummary();
    setCurrentPage(1);
    fetchRows(1, searchName, issueType);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const openModal = (patient: PatientGapRow) => {
    setSelectedPatient(patient);
    setModalOpen(true);
  };

  const handleModalSuccess = (patientId: number) => {
    setRows(prev =>
      prev.map(r => (r.id === patientId ? { ...r, data_complete: true } : r)),
    );
    fetchSummary();
  };

  const showToast = (message: string, severity: ToastSeverity) =>
    setToast({ open: true, message, severity });

  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));

  return (
    <>
      {/* Page title */}
      <div className="pageTitle mb-4">Data Completion Queue</div>

      {/* ---- Summary stat cards ---- */}
      <div className="dcq-stats-row">
        <StatCard
          label="Total Patients"
          value={summary?.total_patients}
          loading={summaryLoading}
          colorClass="blue"
          icon={<AssignmentIcon fontSize="small" />}
        />
        <StatCard
          label="Incomplete Data"
          value={summary?.incomplete}
          loading={summaryLoading}
          colorClass="orange"
          icon={<WarningIcon fontSize="small" />}
        />
        <StatCard
          label="SSN Issues"
          value={summary?.ssn_issues}
          loading={summaryLoading}
          colorClass="red"
          icon={<ErrorIcon fontSize="small" />}
        />
        <StatCard
          label="Field Issues"
          value={summary?.field_issues}
          loading={summaryLoading}
          colorClass="purple"
          icon={<AccountCircleIcon fontSize="small" />}
        />
      </div>

      {/* ---- Filter bar ---- */}
      {/* <div className="dcq-filter-bar" onKeyDown={handleKeyDown}>
        <div className="dcq-filter-left">
          <TextField
            label="Search Patient"
            value={searchName}
            onChange={e => setSearchName(e.target.value)}
            size="small"
            sx={{
              width: 240,
              '& .MuiInputBase-root': { borderRadius: '8px', height: '40px' },
            }}
          />
          <FormControl
            size="small"
            sx={{ minWidth: 200, '& .MuiInputBase-root': { borderRadius: '8px', height: '40px' } }}
          >
            <InputLabel>Issue Type</InputLabel>
            <Select
              label="Issue Type"
              value={issueType}
              onChange={e => setIssueType(e.target.value)}
            >
              {ISSUE_TYPE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            sx={{
              height: '40px',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#1f8cff',
              color: '#1f8cff',
              '&:hover': { background: '#eff6ff', borderColor: '#0070e0' },
            }}
          >
            Search
          </Button>
        </div>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          sx={{
            height: '40px',
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#e2e8f0',
            color: '#475569',
            '&:hover': { background: '#f8fafc', borderColor: '#94a3b8' },
          }}
        >
          Refresh
        </Button>
      </div> */}

      {/* ---- Table ---- */}
      {/* ---- Table ---- */}
      <div className="dcq-table-card">
        {tableLoading ? (
          <div className="dcq-center-state">
            <CircularProgress size={36} sx={{ color: '#1f8cff' }} />
            <span style={{ fontSize: '14px' }}>Loading patients…</span>
          </div>
        ) : tableError ? (
          <div className="dcq-center-state">
            <ErrorIcon sx={{ fontSize: 48, color: '#ef4444', opacity: 0.5 }} />
            <span style={{ fontSize: '14px', color: '#ef4444' }}>
              Failed to load data. Please try refreshing.
            </span>
            <Button variant="outlined" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="dcq-center-state">
            <CheckCircleIcon sx={{ fontSize: 52, color: '#22c55e', opacity: 0.5 }} />
            <span style={{ fontSize: '14px' }}>No patients with data gaps found.</span>
          </div>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '22%', fontWeight: 600, color: '#475569' }}>
                      Patient
                    </TableCell>
                    <TableCell sx={{ width: '14%', fontWeight: 600, color: '#475569' }}>
                      DOB
                    </TableCell>
                    <TableCell sx={{ width: '46%', fontWeight: 600, color: '#475569' }}>
                      Issues
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ width: '18%', fontWeight: 600, color: '#475569' }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow key={row.id}>
                      {/* Patient name */}
                      <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {row.first_name} {row.last_name}
                      </TableCell>

                      {/* DOB */}
                      <TableCell sx={{ color: '#475569' }}>
                        {formatDob(row.dob)}
                      </TableCell>

                      {/* Issues */}
                      <TableCell>
                        {row.issues && row.issues.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {row.issues.map((issue, i) => (
                              <span
                                key={i}
                                className={`dcq-badge ${getBadgeClass(issue)}`}
                              >
                                {issue}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '13px' }}>—</span>
                        )}
                      </TableCell>

                      {/* Action */}
                      <TableCell align="center">
                        {!row.data_complete ? (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => openModal(row)}
                            sx={{
                              borderRadius: '7px',
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: '12px',
                              whiteSpace: 'nowrap',
                              px: 2,
                              py: 0.6,
                              background: 'linear-gradient(135deg, #1f8cff 0%, #0070e0 100%)',
                              boxShadow: '0 2px 6px rgba(31,140,255,0.25)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #0070e0 0%, #005bb5 100%)',
                              },
                            }}
                          >
                            Complete data
                          </Button>
                        ) : (
                          <Tooltip title="Data already complete">
                            <span
                              style={{
                                fontSize: '12px',
                                color: '#94a3b8',
                                fontStyle: 'italic',
                              }}
                            >
                              Done
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <div className="dcq-pagination-row">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    fontWeight: 500,
                  },
                  '& .MuiPaginationItem-root.Mui-selected': {
                    background: 'linear-gradient(135deg, #1f8cff 0%, #0070e0 100%)',
                  },
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* ---- Complete Data Modal ---- */}
      <CompleteDataModal
        open={modalOpen}
        patient={selectedPatient}
        onClose={() => setModalOpen(false)}
        onSuccess={handleModalSuccess}
        onToast={showToast}
      />

      {/* ---- Toast ---- */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ borderRadius: '10px', fontWeight: 500 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DataCompletionQueue;
